import React, { useState, useEffect } from 'react';
import { Box, Typography, Tooltip, useTheme } from '@mui/material';
import { AccessTime as AccessTimeIcon } from '@mui/icons-material';

interface TimeSinceCellProps {
  dateString: string | null | undefined;
  label: 'created' | 'updated';
  displayDate?: string; // Full formatted date for tooltip
}

/**
 * TimeSinceCell Component
 * 
 * Displays a clock icon with color-coded time since a given date.
 * Colors indicate recency:
 * - Green: Very recent (seconds/minutes)
 * - Light Green: Within day (hours)
 * - Amber: Within week (days)
 * - Orange: Within month (weeks)
 * - Red: Older (months/years)
 */
const TimeSinceCell: React.FC<TimeSinceCellProps> = ({ dateString, label, displayDate }) => {
  const theme = useTheme();
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every minute for real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  // Helper function to calculate time since a date and get color
  const getTimeSince = (dateString: string | null | undefined): { text: string; color: string } => {
    if (!dateString) return { text: 'N/A', color: theme.palette.text.secondary };
    
    try {
      const date = new Date(dateString);
      const now = currentTime;
      const diffMs = now.getTime() - date.getTime();
      
      // Handle future dates
      if (diffMs < 0) {
        return { text: 'Just now', color: '#4caf50' };
      }

      const diffSeconds = Math.floor(diffMs / 1000);
      const diffMinutes = Math.floor(diffSeconds / 60);
      const diffHours = Math.floor(diffMinutes / 60);
      const diffDays = Math.floor(diffHours / 24);
      const diffWeeks = Math.floor(diffDays / 7);
      const diffMonths = Math.floor(diffDays / 30);
      const diffYears = Math.floor(diffDays / 365);

      let text: string;
      let color: string;

      if (diffSeconds < 60) {
        text = `${diffSeconds} second${diffSeconds !== 1 ? 's' : ''} ago`;
        color = '#4caf50'; // Green - very recent
      } else if (diffMinutes < 60) {
        text = `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
        color = '#4caf50'; // Green - recent
      } else if (diffHours < 24) {
        text = `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
        color = '#8bc34a'; // Light green - within day
      } else if (diffDays < 7) {
        text = `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
        color = '#ffc107'; // Amber - within week
      } else if (diffWeeks < 4) {
        text = `${diffWeeks} week${diffWeeks !== 1 ? 's' : ''} ago`;
        color = '#ff9800'; // Orange - within month
      } else if (diffMonths < 12) {
        text = `${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`;
        color = '#f44336'; // Red - older
      } else {
        text = `${diffYears} year${diffYears !== 1 ? 's' : ''} ago`;
        color = '#d32f2f'; // Dark red - very old
      }

      return { text, color };
    } catch (error) {
      return { text: 'Invalid date', color: theme.palette.error.main };
    }
  };

  const timeSince = getTimeSince(dateString);
  const tooltipTitle = displayDate 
    ? `${label === 'created' ? 'Created' : 'Last updated'}: ${displayDate}`
    : timeSince.text;

  return (
    <Tooltip title={tooltipTitle}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <AccessTimeIcon 
          sx={{ 
            fontSize: '1rem', 
            color: timeSince.color,
            verticalAlign: 'middle'
          }} 
        />
        <Typography 
          variant="body2" 
          sx={{ 
            fontSize: '0.875rem',
            color: timeSince.color,
            fontWeight: 500
          }}
        >
          {timeSince.text}
        </Typography>
      </Box>
    </Tooltip>
  );
};

export default TimeSinceCell;

